import type { VideoEntry, PlaylistSummary, PlaylistDetail, GroupSummary, GroupDetail } from '../types';

const BASE = ''; // 相对路径，Vite proxy 或 Cloudflare 同源

export async function fetchVideos(withPlaylists = true): Promise<VideoEntry[]> {
  const params = new URLSearchParams();
  if (withPlaylists) params.set('with_playlists', 'true');
  const res = await fetch(`${BASE}/api/videos?${params.toString()}`);
  if (!res.ok) throw new Error(`Failed to fetch videos: ${res.status}`);
  const data = await res.json();
  return data as VideoEntry[];
}

export async function fetchPlaylists(): Promise<PlaylistSummary[]> {
  const res = await fetch(`${BASE}/api/playlists`);
  if (!res.ok) throw new Error(`Failed to fetch playlists: ${res.status}`);
  const data = await res.json();
  return data as PlaylistSummary[];
}

export async function fetchPlaylistDetail(id: string): Promise<PlaylistDetail> {
  const res = await fetch(`${BASE}/api/playlists/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(`Failed to fetch playlist: ${res.status}`);
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return data as PlaylistDetail;
}

export async function deleteVideo(fileId: string): Promise<boolean> {
  const res = await fetch(`${BASE}/api/delete-video`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ file_id: fileId }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Delete failed: ${res.status}`);
  }
  const data = await res.json() as { ok: boolean };
  return data.ok;
}

export async function deleteVideos(fileIds: string[]): Promise<number> {
  const res = await fetch(`${BASE}/api/batch-delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ file_ids: fileIds }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Batch delete failed: ${res.status}`);
  }
  const data = await res.json() as { ok: boolean; deleted: number };
  return data.deleted;
}

// ── 分组 API ──

export interface GroupsResponse {
  custom: GroupSummary[];
  date: GroupSummary[];
  type: GroupSummary[];
}

export async function fetchGroups(): Promise<GroupsResponse> {
  const res = await fetch(`${BASE}/api/groups`);
  if (!res.ok) throw new Error(`Failed to fetch groups: ${res.status}`);
  return res.json() as Promise<GroupsResponse>;
}

export async function fetchGroupDetail(id: string): Promise<GroupDetail> {
  const res = await fetch(`${BASE}/api/groups/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(`Failed to fetch group: ${res.status}`);
  return res.json() as Promise<GroupDetail>;
}

export async function createGroup(name: string, description?: string): Promise<{ ok: boolean; id: string }> {
  const res = await fetch(`${BASE}/api/groups`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, description }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Create group failed: ${res.status}`);
  }
  return res.json() as Promise<{ ok: boolean; id: string }>;
}

export async function deleteGroup(id: string): Promise<boolean> {
  const res = await fetch(`${BASE}/api/groups/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Delete group failed: ${res.status}`);
  }
  const data = await res.json() as { ok: boolean };
  return data.ok;
}

export async function addToGroup(groupId: string, fileId: string): Promise<boolean> {
  const res = await fetch(`${BASE}/api/groups/${encodeURIComponent(groupId)}/add`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ file_id: fileId }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Add to group failed: ${res.status}`);
  }
  const data = await res.json() as { ok: boolean };
  return data.ok;
}

export async function removeFromGroup(groupId: string, fileId: string): Promise<boolean> {
  const res = await fetch(`${BASE}/api/groups/${encodeURIComponent(groupId)}/remove`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ file_id: fileId }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Remove from group failed: ${res.status}`);
  }
  const data = await res.json() as { ok: boolean };
  return data.ok;
}

export async function updateGroupBatch(
  groupId: string,
  opts: { add_file_ids?: string[]; remove_file_ids?: string[] }
): Promise<boolean> {
  const body: Record<string, unknown> = {};
  if (opts.add_file_ids) body.add_file_ids = opts.add_file_ids;
  if (opts.remove_file_ids) body.remove_file_ids = opts.remove_file_ids;
  const res = await fetch(`${BASE}/api/groups/${encodeURIComponent(groupId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Batch update failed: ${res.status}`);
  }
  const data = await res.json() as { ok: boolean };
  return data.ok;
}

// ── 上传 ──

export interface UploadResult {
  ok: boolean;
  file_id?: string;
  file_name?: string;
  file_size?: number;
  mime_type?: string;
  url?: string;
  error?: string;
}

export async function uploadFile(
  file: File,
  onProgress?: (loaded: number, total: number) => void
): Promise<UploadResult> {
  const formData = new FormData();
  formData.append('file', file, file.name);

  // Use XMLHttpRequest for progress tracking
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(e.loaded, e.total);
      }
    });

    xhr.addEventListener('load', () => {
      try {
        const data = JSON.parse(xhr.responseText) as UploadResult;
        if (xhr.status >= 200 && xhr.status < 300 && data.ok) {
          resolve(data);
        } else {
          reject(new Error(data.error || `Upload failed: ${xhr.status}`));
        }
      } catch {
        reject(new Error('Failed to parse upload response'));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Network error')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

    xhr.open('POST', `${BASE}/api/upload`);
    xhr.send(formData);
  });
}
